import { ArrowLeft } from "lucide-react";
import { getLang } from "../i18n";

const LINK = "font-semibold text-sky-700 underline underline-offset-2";

const backLabels = {
  it: "Torna al sito",
  en: "Back to site",
};

export function Layout({ title, updated, children }) {
  const back = backLabels[getLang()] || backLabels.it;
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 antialiased">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3" data-testid="legal-home-link">
            <img src="/ustp-logo.png" alt="USTP" className="h-11 w-11 rounded-xl object-cover ring-1 ring-slate-200" />
            <span className="leading-none">
              <span className="block font-display font-bold text-lg">USTP</span>
              <span className="block font-mono text-[9px] tracking-[0.18em] text-slate-500 mt-0.5">
                UNIVERSE SCORE TOKEN PROTOCOL
              </span>
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-semibold px-4 py-2 hover:bg-sky-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {back}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-14">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{updated}</p>
        <div className="mt-8 space-y-6 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm leading-relaxed text-slate-600 text-sm sm:text-base">
          {children}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 px-5 text-center">
          <img
            src="/images/siae.jpg"
            alt="SIAE — Società Italiana degli Autori ed Editori"
            data-testid="legal-siae-logo"
            className="h-11 w-11 rounded-md object-cover ring-1 ring-slate-200"
          />
          <p className="font-mono text-xs text-slate-400">© {new Date().getFullYear()} USTP — Universe Score Token Protocol · N. 22562423 · 04/04/2025</p>
        </div>
      </footer>
    </div>
  );
}

const Mail = () => (
  <a href="mailto:info@ustp.it" className={LINK}>
    info@ustp.it
  </a>
);

const privacy = {
  it: (
    <>
      <p><strong>Informativa sul trattamento dei dati personali (Reg. UE 2016/679 — GDPR).</strong></p>
      <p><strong>Chi tratta i dati.</strong> Il Team USTP (Universe Score Token Protocol), contattabile all'indirizzo <Mail />, titolare del trattamento.</p>
      <p><strong>Quali dati raccogliamo.</strong> Raccogliamo i dati che ci fornisci volontariamente quando ci contatti via email (es. nome, indirizzo email ed eventuali informazioni che decidi di comunicarci). I dati sono registrati in un archivio interno accessibile solo al team USTP.</p>
      <p><strong>Finalità.</strong> Rispondere alle richieste di assistenza, gestire la partecipazione dei Pionieri e l'accesso al gruppo dedicato (WhatsApp/Telegram).</p>
      <p><strong>Base giuridica.</strong> L'esecuzione di misure precontrattuali o contrattuali su richiesta dell'interessato.</p>
      <p><strong>Conservazione.</strong> I dati sono conservati per il tempo necessario a gestire le richieste e i conseguenti adempimenti di legge.</p>
      <p><strong>Cookie.</strong> Il sito non utilizza cookie di tracciamento: consulta la <a href="/cookie" className={LINK}>Cookie Policy</a>.</p>
      <p><strong>Diritti dell'interessato.</strong> Puoi chiedere in ogni momento l'accesso, la rettifica o la cancellazione dei tuoi dati scrivendo a <Mail />. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali.</p>
    </>
  ),
  en: (
    <>
      <p><strong>Personal data processing notice (EU Reg. 2016/679 — GDPR).</strong></p>
      <p><strong>Who processes the data.</strong> The USTP Team (Universe Score Token Protocol), reachable at <Mail />, is the data controller.</p>
      <p><strong>What data we collect.</strong> We collect the data you voluntarily provide when you contact us by email (e.g. name, email address and any information you choose to share). The data is recorded in an internal archive accessible only to the USTP team.</p>
      <p><strong>Purpose.</strong> To respond to support requests, manage Pioneers' participation and access to the dedicated group (WhatsApp/Telegram).</p>
      <p><strong>Legal basis.</strong> The performance of pre-contractual or contractual measures at the request of the data subject.</p>
      <p><strong>Retention.</strong> Data is kept for the time necessary to handle requests and the related legal obligations.</p>
      <p><strong>Cookies.</strong> The site does not use tracking cookies: see the <a href="/cookie" className={LINK}>Cookie Policy</a>.</p>
      <p><strong>Rights of the data subject.</strong> You can request access, rectification or deletion of your data at any time by writing to <Mail />. You also have the right to lodge a complaint with the data protection authority.</p>
    </>
  ),
};

const cookie = {
  it: (
    <>
      <p><strong>Cookie Policy — versione semplificata.</strong></p>
      <p>Questo sito <strong>non utilizza cookie di profilazione</strong>, né cookie di tracciamento di terze parti a fini pubblicitari o di analisi comportamentale.</p>
      <p><strong>Cosa salviamo sul tuo dispositivo.</strong> Il sito utilizza esclusivamente la memoria locale del browser (localStorage/sessionStorage) per: la preferenza linguistica (IT/EN) e la chiusura degli avvisi informativi temporanei. Non vengono memorizzati dati personali.</p>
      <p><strong>Servizi di terze parti.</strong> Il sito carica caratteri da Google Fonts e contiene collegamenti esterni (WhatsApp, Google Drive, email). I contenuti di terze parti sono soggetti alle rispettive policy.</p>
      <p><strong>Come gestire le preferenze.</strong> Puoi cancellare i dati salvati o bloccarli in qualsiasi momento dalle impostazioni del tuo browser (sezione privacy/cookies).</p>
      <p>Per qualsiasi domanda scrivi a <Mail />.</p>
    </>
  ),
  en: (
    <>
      <p><strong>Cookie Policy — simplified version.</strong></p>
      <p>This site <strong>does not use profiling cookies</strong>, nor third-party tracking cookies for advertising or behavioural analysis purposes.</p>
      <p><strong>What we save on your device.</strong> The site uses exclusively the browser's local storage (localStorage/sessionStorage) for: the language preference (IT/EN) and the dismissal of temporary informational notices. No personal data is stored.</p>
      <p><strong>Third-party services.</strong> The site loads fonts from Google Fonts and contains external links (WhatsApp, Google Drive, email). Third-party content is subject to their respective policies.</p>
      <p><strong>How to manage preferences.</strong> You can delete the saved data or block it at any time from your browser settings (privacy/cookies section).</p>
      <p>For any questions write to <Mail />.</p>
    </>
  ),
};

const titles = {
  privacy: { it: "Privacy Policy", en: "Privacy Policy" },
  cookie: { it: "Cookie Policy", en: "Cookie Policy" },
  updated: {
    it: "Ultimo aggiornamento · 2026",
    en: "Last updated · 2026",
  },
};

export default function LegalPage({ kind }) {
  const lang = getLang();
  const isPrivacy = kind === "privacy";
  const title = (isPrivacy ? titles.privacy : titles.cookie)[lang];
  return (
    <Layout title={title} updated={titles.updated[lang]}>
      {isPrivacy ? privacy[lang] : cookie[lang]}
    </Layout>
  );
}
